import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useConsultationList } from '../hooks/useConsultationList';
import { doctorService } from '../../../services/doctorService';
import { useAuth } from '../../../contexts/AuthContext';
import type { MedicalConsultation, Doctor } from '../../../types/api';
import EmptyState from '../../../components/ui/EmptyState';
import SkeletonList from '../../../components/ui/SkeletonList';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import { parseApiDate } from '../../../lib/format';
import ConsultationStatusBadge from '../components/ConsultationStatusBadge';
import { canDeleteConsultation, canEditConsultation } from '../hooks/useConsultationAccess';

const ConsultationListPage: React.FC = () => {
  const { user } = useAuth();
  const isDoctor = !!user?.doctor;
  const {
    consultations,
    loading,
    error,
    filters,
    pagination,
    fetchConsultations,
    deleteConsultation,
    goToPage,
    updateFilters,
    updateSearch,
    clearFilters,
  } = useConsultationList();

  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [errorDismissed, setErrorDismissed] = useState(false);

  useEffect(() => {
    fetchConsultations();
    doctorService.getAll(100).then(res => setDoctors(res.data || [])).catch(() => { void 0; });
  }, []);

  useEffect(() => {
    if (error) setErrorDismissed(false);
  }, [error]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = (parseApiDate(dateString) ?? new Date(NaN));
    return date.toLocaleDateString('es-DO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return '-';
    const date = (parseApiDate(dateString) ?? new Date(NaN));
    return date.toLocaleTimeString('es-DO', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const hasActiveFilters = filters.search || filters.doctor_id || filters.date_from || filters.date_to || filters.requires_admission !== undefined;

  return (
    <>
      {/* Page Header */}
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Consultas Médicas' }]} />
            <h3 className="page-title mb-0" style={{ fontWeight: 600, color: '#1e293b' }}>Consultas Médicas</h3>
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              {consultations.length > 0
                ? `${consultations.length} consulta${consultations.length === 1 ? '' : 's'} ${hasActiveFilters ? 'filtrada' : 'cargada'}${hasActiveFilters ? '' : 's'}`
                : 'Historial clínico de encuentros clínicos'}
            </p>
          </div>
          <div className="col-sm-6 text-end">
            {isDoctor && (
            <Link to="/consultations/new" className="btn btn-primary">
              <i className="fa fa-plus me-1"></i> Nueva Consulta
            </Link>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-0">
                    <label className="form-label">Buscar</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Paciente, motivo..."
                      value={searchInput}
                      onChange={e => {
                        setSearchInput(e.target.value);
                        updateSearch(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="mb-0">
                    <label className="form-label">Doctor</label>
                    <select
                      className="form-control"
                      value={filters.doctor_id || ''}
                      onChange={e => updateFilters({ doctor_id: e.target.value ? Number(e.target.value) : undefined })}
                    >
                      <option value="">Todos</option>
                      {doctors.map(d => (
                        <option key={d.id} value={d.id}>
                          Dr. {d.first_name} {d.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="mb-0">
                    <label className="form-label">Desde</label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.date_from || ''}
                      onChange={e => updateFilters({ date_from: e.target.value || undefined })}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="mb-0">
                    <label className="form-label">Hasta</label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.date_to || ''}
                      onChange={e => updateFilters({ date_to: e.target.value || undefined })}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="mb-0">
                    <label className="form-label">Admisión</label>
                    <select
                      className="form-control"
                      value={filters.requires_admission === undefined ? '' : String(filters.requires_admission)}
                      onChange={e => {
                        const val = e.target.value;
                        updateFilters({ requires_admission: val === '' ? undefined : val === 'true' });
                      }}
                    >
                      <option value="">Todas</option>
                      <option value="true">Requiere</option>
                      <option value="false">No requiere</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-1 d-flex align-items-end">
                  {hasActiveFilters && (
                    <button
                      className="btn btn-light btn-sm"
                      onClick={() => {
                        setSearchInput('');
                        clearFilters();
                      }}
                      title="Limpiar filtros"
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              {error && !errorDismissed && (
                <div className="alert alert-danger alert-dismissible">
                  <button type="button" className="btn-close" onClick={() => setErrorDismissed(true)}></button>
                  {error}
                </div>
              )}

              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Hora</th>
                      <th>Paciente</th>
                      <th>Doctor</th>
                      <th>Motivo de Consulta</th>
                      <th>Admisión</th>
                      <th>Estado</th>
                      <th className="text-end">Acciones</th>
                    </tr>
                  </thead>
                   <tbody>
                     {loading ? (
                       <tr>
                         <td colSpan={8} className="p-0">
                           <SkeletonList rowCount={5} columnCount={8} className="py-3" />
                         </td>
                       </tr>
                     ) : consultations.length === 0 ? (
                       <EmptyState variant="compact" icon="fa-file-lines" title="No hay consultas registradas" action={{ label: 'Crear primera consulta', to: '/consultations/new', icon: 'fa-plus' }} />
                     ) : (
                      consultations.map((c: MedicalConsultation) => (
                        <tr key={c.id}>
                          <td>{formatDate(c.consultation_date)}</td>
                          <td>{formatTime(c.consultation_date)}</td>
                          <td>
                            {c.patient
                              ? `${c.patient.first_name} ${c.patient.last_name}`
                              : '-'}
                          </td>
                          <td>
                            {c.doctor
                              ? `Dr. ${c.doctor.first_name} ${c.doctor.last_name}`
                              : '-'}
                          </td>
                          <td>
                            {c.chief_complaint.length > 60
                              ? `${c.chief_complaint.substring(0, 60)}...`
                              : c.chief_complaint}
                          </td>
                          <td>
                            {c.requires_admission ? (
                              <span className="custom-badge status-orange">Sí</span>
                            ) : (
                              <span className="custom-badge status-grey">No</span>
                            )}
                          </td>
                          <td>
                            <ConsultationStatusBadge status={c.status} signatureMethod={c.signature_method} />
                          </td>
                          <td className="text-end">
                            <Link
                              to={`/consultations/${c.id}`}
                              className="btn btn-sm bg-info-light me-1"
                              title="Ver"
                            >
                              <i className="fa fa-eye"></i>
                            </Link>
                            {canEditConsultation(c) && (
                              <Link
                                to={`/consultations/${c.id}/edit`}
                                className="btn btn-sm bg-success-light me-1"
                                title="Editar"
                              >
                                <i className="fa fa-pencil"></i>
                              </Link>
                            )}
                            {canDeleteConsultation(c, user) && (
                              <button
                                className="btn btn-sm bg-danger-light"
                                title="Eliminar"
                                onClick={() => deleteConsultation(c.id)}
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="row mt-3">
                  <div className="col-md-6 d-flex align-items-center">
                    <span className="text-muted">
                      Página {pagination.currentPage} de {pagination.totalPages}
                      {' '}(Total: {pagination.total} consultas)
                    </span>
                  </div>
                  <div className="col-md-6">
                    <ul className="pagination justify-content-end mb-0">
                      <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => goToPage(1)} disabled={pagination.currentPage === 1}>
                          &laquo;
                        </button>
                      </li>
                      {[...Array(Math.min(pagination.totalPages, 10))].map((_, i) => {
                        const page = i + 1;
                        return (
                          <li key={page} className={`page-item ${pagination.currentPage === page ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => goToPage(page)}>
                              {page}
                            </button>
                          </li>
                        );
                      })}
                      <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => goToPage(pagination.totalPages)}
                          disabled={pagination.currentPage === pagination.totalPages}>
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConsultationListPage;
